/**
 * Shared search param schema and path resolver for instructor section context.
 *
 * Instructor sub-routes (assessment detail, project, team detail, etc.) can
 * optionally carry `sectionId` as a URL search param so that breadcrumbs,
 * "Back to Course" links, and post-action redirects return to the section
 * dashboard instead of the course-level page.
 */

/** Shape of typed search parameters on instructor sub-routes. */
export interface InstructorSectionSearch {
  sectionId?: string
  reviewerId?: string
  revieweeId?: string
  addReview?: string
  preview?: string
}

/**
 * TanStack Router `validateSearch` implementation for instructor sub-routes.
 * Extracts and coerces `sectionId` while preserving all other search params
 * at runtime (e.g. `mode` on the assessment create routes).
 *
 * @remarks
 * The return type is kept narrow (`InstructorSectionSearch`) so the index
 * signature does not bleed into the router's merged search type. The
 * runtime spread ensures existing params like `mode` are not dropped.
 */
export const validateInstructorSectionSearch = (search: Record<string, unknown>): InstructorSectionSearch => {
  const rawSectionId = search.sectionId
  const rawReviewerId = search.reviewerId
  const rawRevieweeId = search.revieweeId
  const rawAddReview = search.addReview
  const rawPreview = search.preview
  return {
    ...search,
    sectionId: typeof rawSectionId === "string" && rawSectionId.length > 0 ? rawSectionId : undefined,
    reviewerId: typeof rawReviewerId === "string" && rawReviewerId.length > 0 ? rawReviewerId : undefined,
    revieweeId: typeof rawRevieweeId === "string" && rawRevieweeId.length > 0 ? rawRevieweeId : undefined,
    addReview: rawAddReview === "true" || rawAddReview === true ? "true" : undefined,
    preview: rawPreview === "true" || rawPreview === true ? "true" : undefined,
  } as InstructorSectionSearch
}

/** Resolved navigation targets based on section context. */
export interface InstructorSectionTarget {
  /** Path to navigate "back" to — either section or course level. */
  coursePath: string
  /** Whether the target is a section (true) or course page (false). */
  isSection: boolean
}

/**
 * Resolves the instructor "back to course/section" target path.
 *
 * @param courseId - The current course ID from route params.
 * @param sectionId - Optional section ID from search params.
 * @returns The resolved navigation target.
 */
export const resolveInstructorSectionTarget = (courseId: string, sectionId?: string): InstructorSectionTarget => {
  if (sectionId) {
    return {
      coursePath: `/instructor/course/${courseId}/section/${sectionId}`,
      isSection: true,
    }
  }

  return {
    coursePath: `/instructor/course/${courseId}`,
    isSection: false,
  }
}

/**
 * Appends `sectionId` as a search param suffix to a path string.
 * Returns the path unchanged when sectionId is absent.
 *
 * @param path - Base path (may already contain query params).
 * @param sectionId - Optional section ID to append.
 */
export const appendSectionSearch = (path: string, sectionId?: string): string => {
  if (!sectionId) {
    return path
  }

  const separator = path.includes("?") ? "&" : "?"
  return `${path}${separator}sectionId=${encodeURIComponent(sectionId)}`
}
